/**
 * Event envelope — mirrors `schemas/event-envelope.schema.json` (schema_version 1.0).
 *
 * Producers choose a stable `source_event_id`; the server deduplicates on
 * `(tenant_id, source, source_event_id)` and replays the original outcome.
 */
import { z } from 'zod';
export const EVENT_SCHEMA_VERSION = '1.0';
export const actorSchema = z
    .object({
    type: z.enum(['user', 'agent', 'session', 'integration', 'system']),
    id: z.string().min(1),
})
    .strict();
const isoDateTime = z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'must be an ISO-8601 date-time' });
export const eventEnvelopeSchema = z
    .object({
    schema_version: z.literal(EVENT_SCHEMA_VERSION),
    event_id: z.string().uuid(),
    source: z.string().min(1),
    source_event_id: z.string().min(1),
    type: z
        .string()
        .regex(/^[a-z0-9]+(?:[._-][a-z0-9]+)+$/, 'must be a dotted lowercase event type'),
    tenant_id: z.string().min(1),
    dcc_project_id: z.string().uuid(),
    correlation_id: z.string().optional(),
    causation_id: z.string().optional(),
    actor: actorSchema.optional(),
    occurred_at: isoDateTime,
    received_at: isoDateTime,
    payload: z.record(z.unknown()),
})
    .strict();
/** Ingest accepts the envelope without `received_at`; the server stamps it. */
export const ingestEnvelopeSchema = eventEnvelopeSchema
    .omit({ received_at: true })
    .extend({ received_at: isoDateTime.optional() });
export const ingestRequestSchema = z
    .object({ events: z.array(ingestEnvelopeSchema).min(1).max(500) })
    .strict();
export const ingestResultSchema = z.object({
    accepted: z.number(),
    duplicates: z.number(),
    rejected: z.array(z.object({ source_event_id: z.string(), reason: z.string() })),
    results: z.array(z.object({
        source_event_id: z.string(),
        event_id: z.string(),
        outcome: z.enum(['accepted', 'duplicate', 'held', 'rejected']),
        detail: z.string().nullable(),
    })),
});
/** Event types DCC understands. Unknown types are stored as provenance only. */
export const KNOWN_EVENT_TYPES = [
    'build.completed',
    'build.started',
    'deployment.updated',
    'agent.status.changed',
    'session.status.changed',
    'capacity.observed',
    'work_item.status.changed',
    'journey.node.updated',
    'orchestrator.lease.acquired',
    'orchestrator.lease.renewed',
    'orchestrator.lease.released',
    'orchestrator.lease.taken_over',
    'orchestrator.fencing.rejected',
    'orchestrator.split_brain.detected',
    'connection.health.changed',
    'repository.branch.updated',
    'repository.commit.observed',
    'pull_request.updated',
    'decision.opened',
    'decision.resolved',
    'blocker.opened',
    'blocker.resolved',
    'handoff.proposed',
    'handoff.completed',
    'station.approved',
    'governor.recommended',
    'dispatch.blocked',
];
/** SSE projection deltas — `architecture/02-data-model-and-api.md`. */
export const LIVE_CHANNELS = [
    'portfolio.updated',
    'project.updated',
    'journey.updated',
    'capacity.updated',
    'lease.updated',
    'incident.updated',
];
//# sourceMappingURL=events.js.map